import * as chartjs from "chart.js";
import { ChartOptions } from "chart.js";
import moment from "moment";
import { Collection } from "../../Model/Collection";
import { DateRange } from "../../Model/Date";
import { ETemperatureSensorType } from "../../Model/ETemperatureSensorType";
import { TemperatureLog, TemperatureSensor } from "../../Model/Temperature";
import "./temperature.css";
import "./TemperatureChart.css";
import TemperatureGraphColor from "./TemperatureGraphColor";

export enum EGraphType {
  AllGraph,
  AverageMinMax,
  SensorAverage,
  AverageOutput,
  Custom,
}

export interface TemperatureSetting {
  showGraph: EGraphType;
  minTemp: number;
  maxTemp: number;
  minOutput: number;
  maxOutput: number;
  showTemperatureInfo: boolean;
  showTooltip: boolean;

  /**
   * Interval in minute for reloading page.
   * Page reload is useful to prevent chart component
   * from storing too much temperature data point
   * after long period of time.
   */
  refreshInterval: number;

  /**
   * Interval in second to add new temperature data point
   * when there is no new data received from backend.
   */
  refreshStaleDataInterval: number;

  xZoom: number;
  useBestYZoom: boolean;
}

class TemperatureChartUtil {
  static getInitialChartOption = (
    visibleXMin: moment.Moment,
    visibleXMax: moment.Moment,
    temperatureSetting: TemperatureSetting,
    timeStepSize: number = 5,
    datasets: chartjs.ChartDataSets[] | undefined,
    minValues: { [index: string]: number },
    maxValues: { [index: string]: number },
    temperatureSensorCollection: Collection<TemperatureSensor>,
  ): ChartOptions => {
    let minY = temperatureSetting.minTemp;
    let maxY = temperatureSetting.maxTemp;
    if (temperatureSetting.useBestYZoom && datasets !== undefined){
      let {min, max} = TemperatureChartUtil.getMinMaxBestYZoom(minValues, maxValues, datasets, temperatureSensorCollection);
      minY = min;
      maxY = max;
    }
    return {
      animation: {
        duration: 0,
      },
      maintainAspectRatio: false,
      legend: {
        display: false,
      },
      tooltips: {
        enabled: true,
      },
      elements: {
        point: {
          radius: 1,
        }, 
        line: {
          tension: 0,
          stepped: true,
        },
      },
      scales: {
        xAxes: [
          {
            type: "time",
            distribution: "linear",
            time: {
              unit: "minute",
              unitStepSize: timeStepSize,
              displayFormats: {
                minute: "YY-MM-DD hh:mm A",
              },
            },
            display: true,
            ticks: {
              min: visibleXMin,
              max: visibleXMax,
            },
          },
        ],
        yAxes: [
          {
            type: "linear",
            display: true,
            position: "left",
            id: "temperature",
            gridLines: {
              display: true,
              color: "#C4C4C4",
              drawTicks: true,
            },
            // labels: {
            //   show: true,
            // },
            scaleLabel: {
              // stacked: true,
              display: true,
              labelString: "Temperature (°C)",
              fontColor: "#000",
              fontStyle: "bold",
              fontFamily: "Roboto",
              fontSize: 12,
              lineHeight: "14px",
            },

            ticks: {
              min: minY,
              max: maxY,
              stepSize: 5,
              fontColor: "black",
              callback: function (value: number): string {
                return value.toFixed(1);
              },
            },
          },
          {
            type: "linear",
            display: true,
            position: "right",
            id: "output",
            gridLines: {
              display: false,
            },
            scaleLabel: {
              display: true,
              labelString: "Output (%)",
              // direction: "top",
              fontFamily: "Roboto",
              fontSize: 12,
              lineHeight: "14px",
              fontColor: "#000",
              fontStyle: "bold",
            },
            ticks: {
              min: temperatureSetting.minOutput,
              max: temperatureSetting.maxOutput,
              stepSize: 20,
              fontColor: "black",
              callback: function (value: number): string {
                if (value % 40 === 0) return "";
                return value.toString();
              },
            },
          },
        ],
      },
      plugins: {
        datalabels: {
          display: false,
        },
      },
    };
  };

  static getZoom(level: number): [number, number] {
    switch (level) {
      case 0:
        return [5, 1];
      case 1:
        return [10, 2];
      case 2:
        return [30, 3];
      case 3:
        return [60, 7.5];
      case 4:
        return [120, 15];
      case 5:
        return [240, 30];
      case 6:
        return [480, 60];
      case 7:
        return [720, 120];
      case 8:
        return [1440, 240];
      default:
        return [30, 5];
    }
  }

  static getMinValues(sensorNames: string[]) {
    let result: { [index: string]: number } = {};
    for (const sensorName of sensorNames) {
      result[sensorName] = Number.MAX_VALUE;
    }
    return result;
  }

  static getMaxValues(sensorNames: string[]) {
    let result: { [index: string]: number } = {};
    for (const sensorName of sensorNames) {
      result[sensorName] = Number.MIN_VALUE;
    }
    return result;
  }

  static getVisibleDatesFromXZoom(xZoom: number, chartDateRange: DateRange, currentScrollTime: moment.Moment, isLiveChart: boolean) {
    const [timeWindow, stepSize] = TemperatureChartUtil.getZoom(xZoom);
    const offset = timeWindow / 2;
    const startOfDay = moment(chartDateRange.start).startOf("day");
    let endOfDay = moment(chartDateRange.end).endOf("day");
    let min = moment(currentScrollTime).subtract(offset, "minutes");
    let max = moment(currentScrollTime).add(offset, "minutes");
    let step = stepSize;

    if(isLiveChart){
      endOfDay = moment().add(TemperatureChartUtil.getLiveIntervalMinutesXZoom(xZoom), "minutes");
    }

    if (xZoom === 8){
      // need add 1 because the calculation of diff is rounded to min
      const days = endOfDay.diff(startOfDay, "days") + 1;
      min = startOfDay;
      max = endOfDay;
      step = stepSize * days;
    } else if (max.isAfter(endOfDay)) {
      max = endOfDay;
      min = moment(endOfDay).subtract(timeWindow, "minutes");
    } else if (min.isBefore(startOfDay)) {
      min = startOfDay;
      max = moment(startOfDay).add(timeWindow, "minutes");
    }

    return {
      min,
      max,
      stepSize: step,
    };
  }

  static getTemperatureSensorData(
    temperatureSensorCollection: Collection<TemperatureSensor>,
    temperatureLog: TemperatureLog,
    minValues: { [index: string]: number },
    maxValues: { [index: string]: number },
    sensorsVisibility: { [index: string]: boolean }
  ) {
    console.log("TemperatureSensorData Calculated");
    let temperatureSensorData: TemperatureSensorData = {
      datasets: [],
    };

    if (temperatureSensorData.datasets == null) {
      throw new Error("Datasets is null");
    }
    // if (result.labels == null) {
    //   throw new Error("Labels is null");
    // }

    let i = 0;
    for (i = 0; i < temperatureSensorCollection.allIds.length; i++) {
      const sensorName = temperatureSensorCollection.allIds[i];
      const visibility =
        temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Feedback ? false
          : sensorsVisibility[sensorName] == null
          ? true
          : sensorsVisibility[sensorName];
      const color = visibility ? TemperatureGraphColor.getTemperatureGraphColor(sensorName) : "transparent";
      const sensor = temperatureSensorCollection.byId[sensorName];
      const axis = sensor.type === ETemperatureSensorType.Output ? "output" : "temperature";

      temperatureSensorData.datasets[i] = {
        fill: false,
        borderColor: color,
        backgroundColor: color,
        yAxisID: axis,
        label: sensorName,
      };
    }

    const temp: Temp[] = [];

    for (let i = 0; i < temperatureLog.data.length; i++) {
      for (let j = 0; j < temperatureLog.sensorNames.length; j++) {
        const value = temperatureLog.data[i].values[j];
        const sensorName = temperatureLog.sensorNames[j];
        if (value > maxValues[sensorName]) {
          maxValues[sensorName] = value;
        }
        if (value < minValues[sensorName]) {
          minValues[sensorName] = value;
        }
        const point: chartjs.ChartPoint = {
          x: moment(temperatureLog.data[i].time),
          y: value,
        };

        if (temp[j] == null) {
          temp[j] = {
            data: [],
          };
        }

        temp[j].data.push(point);
      }
    }

    for (let i = 0; i < temp.length; i++) {
      temperatureSensorData.datasets[i].data = temp[i].data;
    }

    return {
      temperatureSensorData: temperatureSensorData,
      minValues: minValues,
      maxValues: maxValues,
    };
  }

  static getMinMaxBestYZoom(minValues: { [index: string]: number }, maxValues: { [index: string]: number }, datasets: chartjs.ChartDataSets[], temperatureSensorCollection: Collection<TemperatureSensor>){
    let min = Number.MAX_VALUE;
    let max = Number.MIN_VALUE;
    for (let i = 0; i < datasets.length; i++) {
      const sensorName = datasets[i].label;
      if (sensorName == null) {
        throw new Error("Sensor name is null");
      }
      if (temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Output) {
        continue;
      }
      min = minValues[sensorName] < min ? minValues[sensorName] : min;
      max = maxValues[sensorName] > max ? maxValues[sensorName] : max;
    }
    return {min, max};
  }

  static getSensorVisibility(graphType: EGraphType, temperatureSensorCollection: Collection<TemperatureSensor>): { [index: string]: boolean } {
    let newSensorsVisibility: { [index: string]: boolean } = {};
  
    switch (graphType) {
      case EGraphType.AllGraph: {
        for (let i = 0; i < temperatureSensorCollection.allIds.length; i++) {
          const sensorName = temperatureSensorCollection.allIds[i];
          newSensorsVisibility[sensorName] = true;
        }
        break;
      }
      case EGraphType.AverageMinMax: {
        for (let i = 0; i < temperatureSensorCollection.allIds.length; i++) {
          const sensorName = temperatureSensorCollection.allIds[i];
          if (
            temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Average ||
            temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Minimum ||
            temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Maximum
          ) {
            newSensorsVisibility[sensorName] = true;
          } else {
            newSensorsVisibility[sensorName] = false;
          }
        }
        break;
      }
      case EGraphType.SensorAverage: {
        for (let i = 0; i < temperatureSensorCollection.allIds.length; i++) {
          const sensorName = temperatureSensorCollection.allIds[i];
          if (
            temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Average ||
            temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Sensor
          ) {
            newSensorsVisibility[sensorName] = true;
          } else {
            newSensorsVisibility[sensorName] = false;
          }
        }
        break;
      }
      case EGraphType.AverageOutput: {
        for (let i = 0; i < temperatureSensorCollection.allIds.length; i++) {
          const sensorName = temperatureSensorCollection.allIds[i];
          if (
            temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Average ||
            temperatureSensorCollection.byId[sensorName].type === ETemperatureSensorType.Output
          ) {
            newSensorsVisibility[sensorName] = true;
          } else {
            newSensorsVisibility[sensorName] = false;
          }
        }
        break;
      }
      default:
        break;
    }
  
    return newSensorsVisibility;
  }

  static getLiveIntervalMinutesXZoom(xZoom: number){
    switch (xZoom) {
      case 0:
        return 2.5;
      case 1:
        return 5;
      case 2:
        return 10;
      case 3:
        return 20;
      case 4:
        return 40;
      case 5:
        return 80;
      case 6:
        return 160;
      case 7:
        return 320;
      case 8:
        return 640;
      default:
        return 10;
    }
  }
}

export default TemperatureChartUtil;

export type Temp = {
  data: chartjs.ChartPoint[];
};

export interface TemperatureSensorData extends chartjs.ChartData {}
